// src/components/Homepage.jsx
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

const Homepage = () => {
  const [landingContent, setLandingContent] = useState(null);
  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  
  useEffect(() => {
    // Detect tenant from domain
    const hostname = window.location.hostname;
    let tenantSubdomain = 'default';
    
    console.log('Current hostname:', hostname);
    
    // Check for tenant based on hostname
    if (hostname.includes('ytgator')) {
      tenantSubdomain = 'ytgator';
    } else if (hostname.includes('choice')) {
      tenantSubdomain = 'choice';
    } else if (hostname === 'localhost' || hostname === '127.0.0.1') {
      // For local development, default to choice
      tenantSubdomain = 'choice';
    }
    
    console.log('Detected tenant:', tenantSubdomain);
    
    // Load tenant config from API
    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
    
    fetch(`${apiUrl}/api/tenants/by-domain/${tenantSubdomain}`)
      .then(res => {
        if (!res.ok) throw new Error('Failed to load tenant config');
        return res.json();
      })
      .then(data => {
        console.log('Tenant config loaded:', data);
        setTenant(data);
        // Try to load custom landing page
        loadCustomLanding(tenantSubdomain);
      })
      .catch(error => {
        console.error('Error loading tenant config:', error);
        // Still try to load landing page even if tenant config fails
        loadCustomLanding(tenantSubdomain);
      });
  }, []);
  
  const loadCustomLanding = (tenantSubdomain) => {
    console.log('Attempting to load landing page for:', tenantSubdomain);
    
    // Check if custom landing page exists
    fetch(`/landing-pages/${tenantSubdomain}/index.html`)
      .then(response => {
        console.log('Landing page response status:', response.status);
        if (response.ok) {
          return response.text();
        }
        throw new Error('No custom landing page found');
      })
      .then(html => {
        console.log('Custom landing page loaded, size:', html.length);
        setLandingContent(html);
        setLoading(false);
      })
      .catch(error => {
        console.log('No custom landing page, using default React component:', error.message);
        // Use default React component if no custom landing
        setLandingContent(null);
        setLoading(false);
      });
  };
  
  // Show loading state
  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh' 
      }}>
        <div>Loading...</div>
      </div>
    );
  }
  
  // If custom landing page exists, render it
  if (landingContent) {
    return (
      <div 
        className="custom-landing"
        dangerouslySetInnerHTML={{ __html: landingContent }}
      />
    );
  }
  
  // Default React landing page
  return (
    <div className="homepage" style={{ 
      padding: '20px',
      maxWidth: '1200px',
      margin: '0 auto'
    }}>
      <header style={{
        textAlign: 'center',
        padding: '60px 20px',
        background: `linear-gradient(135deg, ${tenant?.primaryColor || '#0066CC'} 0%, ${tenant?.secondaryColor || '#004499'} 100%)`,
        color: 'white',
        borderRadius: '10px',
        marginBottom: '40px'
      }}>
        <h1 style={{ fontSize: '3em', marginBottom: '20px' }}>
          {tenant?.heroTitle || 'Welcome to MCN Platform'}
        </h1>
        <p style={{ fontSize: '1.3em', marginBottom: '30px' }}>
          {tenant?.heroSubtitle || 'Your Multi-Channel Network Solution'}
        </p>
        
        <div style={{ display: 'flex', gap: '15px', justifyContent: 'center' }}>
          <button 
            onClick={() => navigate('/login')}
            style={{
              padding: '12px 30px',
              fontSize: '16px',
              background: 'white',
              color: tenant?.primaryColor || '#0066CC',
              border: 'none',
              borderRadius: '5px',
              cursor: 'pointer',
              fontWeight: 'bold'
            }}
          >
            Login
          </button>
          <button 
            onClick={() => navigate('/register')}
            style={{
              padding: '12px 30px',
              fontSize: '16px',
              background: 'rgba(255,255,255,0.2)',
              color: 'white',
              border: '2px solid white',
              borderRadius: '5px',
              cursor: 'pointer',
              fontWeight: 'bold'
            }}
          >
            Sign Up
          </button>
          <button 
            onClick={() => navigate('/apply')}
            style={{
              padding: '12px 30px',
              fontSize: '16px',
              background: 'transparent',
              color: 'white',
              border: '2px solid white',
              borderRadius: '5px',
              cursor: 'pointer',
              fontWeight: 'bold'
            }}
          >
            Apply as Creator
          </button>
        </div>
      </header>
      
      <section style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
        gap: '30px',
        marginBottom: '60px'
      }}>
        <div style={{ 
          padding: '30px',
          background: 'white',
          borderRadius: '10px',
          boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
        }}>
          <h2>For Creators</h2>
          <p>Join our network and grow your channel with professional support, monetization opportunities, and advanced analytics.</p>
        </div>
        
        <div style={{ 
          padding: '30px',
          background: 'white',
          borderRadius: '10px',
          boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
        }}>
          <h2>For Investors</h2>
          <p>Invest in content creators and earn returns from their success. Transparent revenue sharing and detailed analytics.</p>
        </div>
        
        <div style={{ 
          padding: '30px',
          background: 'white',
          borderRadius: '10px',
          boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
        }}>
          <h2>For Aggregators</h2>
          <p>Bring creators to our platform and earn commissions. Build your network and grow your earnings.</p>
        </div>
      </section>
      
      <footer style={{
        textAlign: 'center',
        padding: '40px 20px',
        borderTop: '1px solid #eee',
        color: '#666'
      }}>
        <p>&copy; 2024 {tenant?.name || 'MCN Platform'}. All rights reserved.</p>
        <div style={{ marginTop: '20px' }}>
          <a href="#" style={{ margin: '0 15px', color: '#666' }}>About</a>
          <a href="#" style={{ margin: '0 15px', color: '#666' }}>Contact</a>
          <a href="#" style={{ margin: '0 15px', color: '#666' }}>Terms</a>
          <a href="#" style={{ margin: '0 15px', color: '#666' }}>Privacy</a>
        </div>
      </footer>
    </div>
  );
};

export default Homepage;